import React from 'react';

import Accordion from 'components/common/accordion';

import SkillPanel from './SkillPanel';

import type { PermissionMap } from 'pages/settings/users/userPermissions/types';
import type { LegacySkill, LegacySkillCategory, SkillFormValues } from '../types';

type Props = {
  isHr?: boolean;
  skills: LegacySkill[];
  onSkillAdd: (skillCategory: LegacySkillCategory) => void;
  activeSkill?: SkillFormValues | null;
  searchQuery: string;
  onSkillCategoryClick: (category: LegacySkillCategory) => void;
  onSkillClick: (skill: LegacySkill) => void;
  rating?: boolean;
  selectedSkills?: LegacySkill[];
  skillCategories?: LegacySkillCategory[];
  permissions?: PermissionMap;
};

const SkillSetV1: React.FC<Props> = ({
  isHr,
  skills,
  onSkillAdd,
  activeSkill,
  searchQuery,
  onSkillCategoryClick,
  onSkillClick,
  rating = false,
  selectedSkills = [],
  skillCategories = [],
  permissions,
  ...rest
}) => {
  const categoryTemplate: Record<string, LegacySkill[]> = {};

  skillCategories.forEach((category) => {
    categoryTemplate[category.name] = [];
  });

  const parseSkills = (items: LegacySkill[]) =>
    items.reduce<Record<string, LegacySkill[]>>((accumulator, currentSkill) => {
      const activeCategory = currentSkill.skillCategory?.name || '';

      return {
        ...accumulator,
        [activeCategory]: accumulator[activeCategory] ? [...accumulator[activeCategory], currentSkill] : [currentSkill],
      };
    }, categoryTemplate);

  const filteredSkills = parseSkills(skills);
  const filteredSelectedSkills = parseSkills(selectedSkills);

  const data = Object.keys(filteredSkills).map((category) => {
    const skillsForCategory = rating
      ? filteredSkills[category]
      : filteredSkills[category].filter((skill) => skill.name.toLowerCase().includes(searchQuery.toLowerCase()));

    const categoryValue = skillCategories.find((skillCategory) => skillCategory.name === category) ?? {
      name: category,
      description: null,
    };

    const Content: React.FC = () => (
      <SkillPanel
        isHr={isHr}
        rating={rating}
        skills={skillsForCategory}
        selectedSkills={filteredSelectedSkills[category]}
        onClick={onSkillClick}
        activeSkill={activeSkill ?? null}
        handleAddClick={() => {
          onSkillAdd(categoryValue);
        }}
        permissions={permissions}
        {...rest}
      />
    );

    return {
      title: category,
      value: {
        id: categoryValue.id ?? null,
        name: categoryValue.name,
        description: categoryValue.description ?? null,
      },
      onTitleClick: () => onSkillCategoryClick(categoryValue),
      content: Content,
      info: skillsForCategory.length,
    };
  });

  return data.length ? (
    <Accordion data={data} className="pr-6" />
  ) : (
    <div className="empty-skill-set">Click on Add category to create a new skill category.</div>
  );
};

export default SkillSetV1;
